import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material';
import { get } from 'lodash';

import { CustomerTypeEnum } from 'common/src/models';
import {
  CreateBranchOfficeModalComponent,
  CreateNewContactModalComponent,
  CreateNewPartnerModalComponent,
  CreateNewPrivatePartnerModalComponent
} from 'common/src/modules/modals/modals-crm';
import { FormInputChangedModel } from 'projects/workspace/src/app/shared/models/form-input-value.model';
import { Contact, GeneralPartnerModel, PartnerModel } from 'projects/workspace/src/app/partners/corporate/models/partner.model';
import { PrivatePartnerModel } from 'projects/workspace/src/app/partners/private/models/partner.model';
import { ChangesStrategy } from 'projects/workspace/src/app/shared/enums/change-strategy.enum';
import { TrackInputChanges } from 'projects/workspace/src/app/shared/decorators/track-input-changes';
import { PartnersApiService } from 'projects/workspace/src/app/partners/corporate/services/partner-api.service';
import { PartnersFieldsChangedModel } from './update-partner-fields.model';
import { CustomSearchFn } from 'common/src/modules/rnpl-common/helpers';
import { SystemPreferencesPermissionsService } from '../../../services/system-preferences-permissions.service';
import { BranchOfficeModel } from 'projects/workspace/src/app/partners/corporate/models';
import { PartnersTypeEnum } from 'projects/workspace/src/app/partners/corporate/enums';
import { OcrPartnerDetailsModel } from 'projects/workspace/src/app/incoming-invoice/models';


@Component({
  selector: 'rnpl-select-partner',
  templateUrl: './select-partner.component.html',
  styleUrls: ['./select-partner.component.scss']
})
export class SelectPartnerComponent implements OnInit, OnChanges, OnDestroy {

  public branchIdControlSubscription: Subscription;
  public corporatePartnersEnabled: boolean = false;
  public privatePartnersEnabled: boolean = false;

  @Input() readonly partnerLabel: string = 'FORM.CUSTOMER';

  @Input() readonly partnerType: FormControl;
  @Input() readonly companyId: FormControl;
  @Input() readonly companyIdFieldName: string = 'company.id';
  @Input() readonly generalPartnerNameFieldName: string = 'generalPartnerName';
  @Input() readonly generalPartnerVatNumberFieldName: string = 'vatNumber';
  @Input() readonly userDefinedPartnerName: boolean = false;
  @Input() readonly generalPartnerAllowed: boolean = false;
  @Input() readonly createPredefinedGeneralPartnerAllowed: boolean = false;
  @Input() readonly ocrPartnerDetails: OcrPartnerDetailsModel;
  @Input() readonly showContactPersonInAddress: boolean = false;
  @Input() readonly companyContactId: FormControl;
  @Input() readonly branchOfficeIdControl: FormControl;
  @Input() readonly email: FormControl;
  @Input() readonly phone: FormControl;
  @Input() readonly userDefinedCustomerControl: FormControl;
  @Input() readonly generalPartnerNameControl: FormControl;
  @Input() readonly generalPartnerVatNumberControl: FormControl;
  @Input() readonly contactTitleControl: FormControl;
  @Input() readonly contactFirstNameControl: FormControl;
  @Input() readonly contactLastNameControl: FormControl;
  // @Input() readonly countryControl: FormControl;
  @Input() readonly companyIdBindValue: string = 'id';
  @Input() readonly partnersFilers: { [key: string]: any } = null;
  @Input() readonly userDefinedPartnerAction: boolean;
  @Input() readonly userDefinedPartnerType: CustomerTypeEnum = CustomerTypeEnum.NOT_SAVED_PARTNER;
  @Input() readonly placeholder: string = 'FORM.SELECT_CUSTOMER';
  @Input() readonly hintsId: string = '';
  @Input() readonly contactPersonIsOptional: boolean;
  @Input() readonly fromModal: boolean;
  @Input() readonly showInfoBlock: boolean;
  @Input() readonly infoBlockTitle: string;
  @Input() readonly infoBlockMsg: string;
  @Input() readonly showValidationErrors: string;
  @Input() readonly branchOfficeName?: string;
  @Input() readonly companyContactName?: string;
  // @Input() countries: CountryModel[];

  @Output() fieldChanged: EventEmitter<FormInputChangedModel> = new EventEmitter<FormInputChangedModel>();
  @Output() partnerChanged: EventEmitter<PartnersFieldsChangedModel> = new EventEmitter<PartnersFieldsChangedModel>();
  @Output() selectedCustomerChanged: EventEmitter<PartnerModel> = new EventEmitter<PartnerModel>();
  // @Output() countryChanged: EventEmitter<FormInputChangedModel> = new EventEmitter<FormInputChangedModel>();

  @ViewChild('userDefinedPartner', {static: false}) userDefinedPartnerRef: ElementRef;

  public displayUserDefinedPartnerName$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public customSearchFn = CustomSearchFn;

  readonly showDropdownSpin$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  readonly combinedPartnersList$: BehaviorSubject<PartnerModel[]> = new BehaviorSubject<PartnerModel[]>([]);
  readonly generalPartnersList$: BehaviorSubject<GeneralPartnerModel[]> = new BehaviorSubject<GeneralPartnerModel[]>([]);
  readonly contactsList$: BehaviorSubject<Contact[]> = new BehaviorSubject<Contact[]>([]);
  readonly branchOfficeList$: BehaviorSubject<BranchOfficeModel[]> = new BehaviorSubject<BranchOfficeModel[]>([]);
  readonly destroy$: Subject<void> = new Subject();

  constructor(
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private partnersApiService: PartnersApiService,
    public systemPreferencesPermissionsService: SystemPreferencesPermissionsService,
  ) { }

  ngOnInit(): void {
    this.showDropdownSpin$.next(true);
    this.getCombinedPartners();
    if (this.generalPartnerAllowed) {
      this.getGeneralPartners();
    }
    this.corporatePartnersEnabled = this.systemPreferencesPermissionsService.corporatePartnersEnabled();
    this.privatePartnersEnabled = this.systemPreferencesPermissionsService.privatePartnersEnabled();
  }

  @TrackInputChanges<string>('companyId', 'updateContactsList', ChangesStrategy.Each)
  @TrackInputChanges<string>('branchOfficeName', 'getBranchOffices', ChangesStrategy.Each)
  @TrackInputChanges<string>('branchOfficeIdControl', 'handleBranchOfficeControl', ChangesStrategy.Each)
  ngOnChanges(changes: SimpleChanges) {}


  private getCombinedPartners(): void {
    this.partnersApiService.getCombinedPartnersList(this.partnersFilers)
      .pipe(takeUntil(this.destroy$))
      .subscribe(partners => {
        this.showDropdownSpin$.next(false);
        this.combinedPartnersList$.next(partners);
        this.updateContactsList();
        this.getBranchOffices();
        if (this.generalPartnerAllowed && !this.generalPartnersList$.getValue().length) {
          this.getGeneralPartners();
        }
      });
  }

  private getGeneralPartners(): void {
    this.partnersApiService.getGeneralPartners()
      .pipe(takeUntil(this.destroy$))
      .subscribe(partners => {
        this.generalPartnersList$.next(partners);
      });
  }

  public updateField(fieldValue: any, fieldName: string): void {
    this.fieldChanged.emit({fieldName, fieldValue});
  }

  // public updateCountryField(fieldValue: any, fieldName: string): void {
  //   this.countryChanged.emit({fieldName, fieldValue});
  // }

  public updatePartner(
    partnerType: CustomerTypeEnum,
    partnerTypeFieldName: string,
    partnerId: number,
    partnerIdFieldName: string,
    salesPriceList: string
  ): void {
    this.partnerChanged.emit({partnerType, partnerTypeFieldName, partnerId, partnerIdFieldName, salesPriceList});
  }

  public selectedCustomer(customer): void {
    this.selectedCustomerChanged.emit(customer);
  }

  public createNewPartner(ocrPartnerDetails?: OcrPartnerDetailsModel) {
    const dialogRef = this.dialog.open(CreateNewPartnerModalComponent, {
      data: {
        ocrPartnerDetails
      },
      disableClose: true
    });

    dialogRef.afterClosed()
      .subscribe((partner: PartnerModel) => {
        if (partner) {
          this.getCombinedPartners();
          this.updatePartnerFields(partner);
          this.companyIdBindValue
            ? this.companyId.setValue(partner.id)
            : this.companyId.setValue(partner);
        }
      });
  }

  public createNewPrivatePartner(ocrPartnerDetails?: OcrPartnerDetailsModel) {
    const dialogRef = this.dialog.open(CreateNewPrivatePartnerModalComponent, {
      data: {
        ocrPartnerDetails
      },
      disableClose: true
    });

    dialogRef.afterClosed()
      .subscribe((partner: PrivatePartnerModel) => {
        if (partner) {
          this.getCombinedPartners();
          this.updatePartnerFields(partner);
          this.companyIdBindValue
            ? this.companyId.setValue(partner.id)
            : this.companyId.setValue(partner);
        }
      });
  }

  public resetContactOnBranchSelect(): void {
    if (this.companyContactId) {
      this.companyContactId.patchValue(null, {emitEvent: false});
    }
  }

  public createNewContact(): void {
    const dialog = this.dialog.open(CreateNewContactModalComponent, {
      data: {
        partner: {
          id: get(this.companyId, this.companyIdBindValue  === '' ? 'value.id' : 'value')
        },
      },
      disableClose: true
    });

    dialog.afterClosed().subscribe((contact: Contact) => {
      if (contact) {
        this.getCombinedPartners();
        this.companyContactId.patchValue(contact.id);
        this.updateField(contact.id, 'companyContact.id');
      }
    });
  }

  public updateContactsList() {
    this.contactsList$.next([]);
    const selectedPartner = this.combinedPartnersList$
      .getValue()
      .find(partner => partner.id === get(this.companyId, this.companyIdBindValue  === '' ? 'value.id' : 'value'));


    if (selectedPartner && (selectedPartner.type === PartnersTypeEnum.CORPORATE)) {
      if (this.branchOfficeIdControl && this.branchOfficeIdControl.value && this.branchOfficeList$.getValue().length) {
        const selectedBranch = this.branchOfficeList$
          .getValue()
          .find(branch => branch.id === this.branchOfficeIdControl.value);

        if (!selectedBranch) { return; }

        let contacts = (selectedBranch.contacts || []);
        // add deleted contact to list
        if (
          this.companyContactId
          && this.companyContactId.value
          && this.companyContactName
          && (!contacts || !contacts.find(c => c.id === this.companyContactId.value))
        ) {
          if (contacts && contacts.length) {
            contacts.push({id: this.companyContactId.value, name: this.companyContactName} as any);
          } else {
            contacts = [{id: this.companyContactId.value, name: this.companyContactName} as any];
          }
        }
        this.contactsList$.next(this.prepareContactsList(contacts));
        return;
      }

      if (selectedPartner && selectedPartner.contacts.length) {
        const contacts = selectedPartner.contacts.filter(contact => !contact.branchId);
        if (
          !this.systemPreferencesPermissionsService.subscriptionPermissions().BRANCH_OFFICES
          && this.companyContactId.value
          && (!contacts || !contacts.find(c => c.id === this.companyContactId.value))
        ) {
          contacts.push({id: this.companyContactId.value, name: this.companyContactName} as any);
        }
        this.contactsList$.next(this.prepareContactsList(contacts));
      }
    }
  }

  public getBranchOffices(selected?): void {
    const partnerId = (selected && selected.id) || get(this.companyId, this.companyIdBindValue  === '' ? 'value.id' : 'value');
    if (
      this.systemPreferencesPermissionsService.subscriptionPermissions().BRANCH_OFFICES
      && partnerId
    ) {
      const selectedPartner = this.combinedPartnersList$
        .getValue()
        .find(partner => partner.id === partnerId);
      if (!selectedPartner || (selectedPartner.type !== PartnersTypeEnum.CORPORATE)) { return; }

      this.partnersApiService.getPartnerBranchOffices(selectedPartner.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe((branches: BranchOfficeModel[]) => {
          if (branches && branches.length) {
            let branchesList = branches;
            // add deleted branch to list
            if (
              this.branchOfficeIdControl
              && this.branchOfficeIdControl.value
              && this.branchOfficeName
              && !branchesList.find(b => b.id === this.branchOfficeIdControl.value)
            ) {
              branchesList.push({id: this.branchOfficeIdControl.value, name: this.branchOfficeName} as BranchOfficeModel);
            }
            branchesList.push({id: 0, name: 'BRANCH_OFFICE.HEADQUARTERS'} as BranchOfficeModel);
            // set old name if it stores in document
            if (this.branchOfficeName && this.branchOfficeIdControl.value) {
              branchesList = branchesList.map(b => {
                if (b.id !== this.branchOfficeIdControl.value) { return b; }
                return {
                  ...b,
                  name: this.branchOfficeName
                };
              });
            }
            this.branchOfficeList$.next(branchesList);
            this.updateContactsList();
          } else {
            this.branchOfficeList$.next([]);
          }
        });
    }
  }

  // show "Main company" as selected when no option is selected
  public handleBranchOfficeControl(): void {
    if (this.branchOfficeIdControl) {
      if (!this.branchOfficeIdControl.value) {
        this.branchOfficeIdControl.patchValue(0, {emitEvent: false});
      }
      if (this.branchIdControlSubscription) {
        this.branchIdControlSubscription.unsubscribe();
      }
      this.branchIdControlSubscription = this.branchOfficeIdControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          if (value === null) {
            this.branchOfficeIdControl.patchValue(0, {emitEvent: false});
            this.updateContactsList();
          }
        });
    }
  }

  public prepareContactsList(contacts): Contact[] {
    return contacts.map(contact => ({
      ...contact,
      name: contact.name
        .replace('Corporate Contact', 'COMMON.CORPORATE_CONTACT')
        .replace('User-defined Contact', 'COMMON.USER_DEFINED_CONTACT')
        .replace('Accounting Contact', 'COMMON.ACCOUNTING_CONTACT')
    }));
  }

  public displayUserDefinedPartner(): void {
    this.updatePartner(this.userDefinedPartnerType, 'partnerType', 0, this.companyIdFieldName, '');
    this.partnerType.setValue(this.userDefinedPartnerType);
    this.displayUserDefinedPartnerName$.next(true);
    this.cdr.detectChanges();
    if (this.userDefinedPartnerRef && this.userDefinedPartnerRef.nativeElement) {
      this.userDefinedPartnerRef.nativeElement.focus();
    }
  }

  get isPartnerTypeCorporate(): boolean {
    return this.partnerType.value === CustomerTypeEnum.CORPORATE_PARTNER;
  }

  get isPartnerTypeGeneral(): boolean {
    return this.partnerType.value === CustomerTypeEnum.GENERAL;
  }

  get isPartnerTypeNotSavedPartner(): boolean {
    return this.partnerType.value === CustomerTypeEnum.NOT_SAVED_PARTNER || this.partnerType.value === CustomerTypeEnum.NOT_SAVED;
  }

  public updatePartnerFields(partner): void {
    this.selectedCustomer(partner);
    this.updatePartner(partner.type, 'partnerType', partner.id, this.companyIdFieldName, partner.salesPriceList);
    this.displayUserDefinedPartnerName$.next(false);
    this.partnerType.setValue(partner.type);
  }

  public selectGeneralPartner(partner: GeneralPartnerModel): void {
    if (!this.companyIdBindValue) {
      this.companyId.patchValue(partner);
    } else {
      this.companyId.patchValue(partner.id);
    }

    this.selectedCustomer(partner);
    this.updatePartner(partner.type as CustomerTypeEnum, 'partnerType', partner.id, this.companyIdFieldName, '');
    this.displayUserDefinedPartnerName$.next(false);
    this.partnerType.setValue(partner.type);
  }

  public createBranchOffice(): void {
    const dialog = this.dialog.open(CreateBranchOfficeModalComponent, {
      data: {
        partnerId: get(this.companyId, this.companyIdBindValue  === '' ? 'value.id' : 'value'),
      },
      disableClose: true
    });

    dialog.afterClosed().subscribe((branchOfficeId: number) => {
      if (branchOfficeId) {
        this.getCombinedPartners();
        this.branchOfficeIdControl.patchValue(branchOfficeId);
        this.updateField(branchOfficeId, 'branchOfficeId');
      }
    });
  }

  get bottomIndent(): number {
    if (this.generalPartnerAllowed && this.generalPartnersList$.getValue().length) {
      return this.generalPartnersList$.getValue().length * 44 + (this.userDefinedPartnerAction ? 124 : 80);
    } else {
      return this.userDefinedPartnerAction ? 124 : 80;
    }
  }

  get getSelectedGeneralPartner(): GeneralPartnerModel {
    if (!this.companyId.value || !this.generalPartnersList$.getValue().length) { return null; }
    return this.generalPartnersList$.getValue().find(gp => (gp.id === this.companyId.value) || (gp.id === this.companyId.value.id));
  }

  // get availableCountries(): CountryModel[] {
  //   if (!this.countries || !this.getSelectedGeneralPartner) { return []; }
  //
  //   return this.countries.filter(c => {
  //     if (this.getSelectedGeneralPartner.generalType === 'eu') { return c.isEu; }
  //     if (this.getSelectedGeneralPartner.generalType === 'non_eu') { return !c.isEu; }
  //     return this.countries;
  //   })
  // }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
